import api from "../../services/api";
import { Eventbus } from "../../utils";
import { events } from "../../utils/constants";
import logger from "../../utils/logger";
import mergeCollection from "../../utils/helpers/mergeCollection";

const InboxService = (function () {
    // Private data cache
    let data = [];
    let isListenerRegistered = false;
    let isLoading = false; // Add loading flag to prevent duplicate requests

    function getData() {
        return [...data]; // Return a copy to prevent external mutation
    }

    function getWinzEntries(id) {
        return api.getWinzEntries(id)
    }

    function updateEmailRecord(updatedRecords) {
        try {
            // Handle both single records and arrays of records
            const recordsArray = Array.isArray(updatedRecords) ? updatedRecords : [updatedRecords];

            if (!recordsArray || recordsArray.length === 0) {
                logger.warn('No records received for update:', updatedRecords);
                return;
            }

            logger.debug('SignalR update received:', {
                recordCount: recordsArray.length,
                currentDataLength: data.length,
                records: recordsArray.map(r => ({ id: r?.id, updatedAt: r?.updatedAt }))
            });

            // Process each record
            recordsArray.forEach(updatedRecord => {
                if (!updatedRecord || !updatedRecord.id) {
                    logger.warn('Invalid record in batch:', updatedRecord);
                    return;
                }

                updatedRecord.exported = updatedRecord.batchId !== undefined && updatedRecord.batchId !== null;

                // Convert updatedAt to updatedAt for the merge utility
                const recordForMerge = {
                    ...updatedRecord,
                    updatedAt: updatedRecord.updatedAt || updatedRecord.dateProcessed || updatedRecord.createdAt
                };

                const beforeLength = data.length;
                const existingRecord = data.find(record => record.id === updatedRecord.id);

                // Use the merge collection utility to handle the update
                // This will properly handle adding new records, updating existing ones,
                // and respecting timestamp logic for updates
                mergeCollection(
                    [recordForMerge], // Source collection (single updated record with correct timestamp field)
                    data, // Target collection (our cached data)
                    {
                        idField: 'id',
                        event: events.INBOX_UPDATED // This will emit the event automatically
                    }
                );

                const afterLength = data.length;
                logger.debug('SignalR merge result for record:', {
                    id: updatedRecord.id,
                    beforeLength,
                    afterLength,
                    wasExisting: !!existingRecord,
                    eventEmitted: events.INBOX_UPDATED
                });
            });

        } catch (error) {
            logger.error('Error updating email records:', error, updatedRecords);
        }
    }

    function deleteEmailRecord(email) {
        const entryExists = data.some((data) => data.id === email.id);
       
        if (entryExists) {
            const deleteItem = { id: email.id, _isDeleted: true }; // Mark as deleted
            mergeCollection([deleteItem], data, { hardDelete: true, idField: "id", event: events.INBOX_UPDATED });
        }
    }

    function loadData(forceRefresh = false, from, to) {
        // Prevent duplicate requests
        if (isLoading && !forceRefresh) {
            return Promise.resolve([...data]);
        }

        // If data exists and we're not forcing a refresh, return the cached data
        if (data.length > 0 && !forceRefresh) {
            isLoading = false; // Ensure loading state is reset when returning cached data
            Eventbus.emit(events.INBOX_LOADED, [...data]);
            return Promise.resolve([...data]);
        }

        isLoading = true;

        // Otherwise fetch fresh data from the API
        return api
            .getEmailRecords(from, to)
            .then((response) => {
                if (response.code === 'ERR_CANCELED') {
                    return data; // Return existing data on cancellation
                }

                // Normalize timestamp fields for merge utility compatibility
                const normalizedData = (response || []).map(record => ({
                    ...record,
                    exported: record.batchId !== undefined && record.batchId !== null,
                    updatedAt: record.updatedAt || record.dateProcessed || record.createdAt
                }));

                data = normalizedData; // Store the normalized data
                Eventbus.emit(events.INBOX_LOADED, [...data]);

                // Only register the event listener once
                if (!isListenerRegistered) {
                    Eventbus.on(events.SIGNALR.WINZ_EMAIL_DATA_CHANGES, updateEmailRecord);
                    Eventbus.on(events.SIGNALR.WINZ_EMAIL_DATA_DELETE, deleteEmailRecord);
                    isListenerRegistered = true;
                    logger.debug('SignalR email data changes listener registered');
                }

                return [...data];
            })
            .catch((error) => {
                logger.error('Error fetching email records:', error);
                return [];
            })
            .finally(() => {
                isLoading = false;
            });
    }

    function init(from, to) {
        return loadData(true, from, to);
    }

    function reprocess(id) {
        return api.reprocess(id);
    }

    function updateWinzEntry(entryId, updatedData) {
        delete updatedData.transactionId
        delete updatedData.surname;
        delete updatedData.firstname;
        delete updatedData.childId;
        delete updatedData.centreName;
        delete updatedData.centreId;
        delete updatedData.centreEceNumber;
        updatedData.ccahours = updatedData.ccahours || "0"; // Ensure ccaHours is set to 0 if not provided
        return api.updateWinzEntry(entryId, updatedData);
    }

    function getEmailPdf(id) {
        return api.getPdf(id);
    }

    function createWinzEntry(entryData) {
        return api.createWinzEntry(entryData);
    }

    function deleteWinzEntry(id) {
        return api.deleteWinzEntry(id);
    }

    function updateGrandTotal(payload) {
        return api.putGrandTotal(payload);
    }

    function resetAll() {
        logger.info('Resetting Inbox Service data');
        return api.resetAll();
    }

    function clear() {
        if (isListenerRegistered) {
            Eventbus.off(updateEmailRecord);
            Eventbus.off(deleteEmailRecord);
            isListenerRegistered = false;
            logger.debug('SignalR email data changes listener removed');
        }
        data = [];
    }

    function getLoadingState() {
        return isLoading;
    }
    return {
        getData,
        getWinzEntries,
        updateWinzEntry,
        loadData,
        init,
        reprocess,
        getEmailPdf,
        createWinzEntry,
        deleteWinzEntry,
        clear,
        updateEmailRecord, // Export for testing and debugging
        getLoadingState, // Export loading state
        updateGrandTotal,
        resetAll
    };
})();

export default InboxService;
